import { Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SECTIONS_WITH_LIST: Array<{
  titleKey: string;
  introKey?: string;
  itemKeys: string[];
  outroKey?: string;
}> = [
  {
    titleKey: 'privacy.s2_title',
    introKey: 'privacy.s2_intro',
    itemKeys: ['privacy.s2_item_1', 'privacy.s2_item_2', 'privacy.s2_item_3', 'privacy.s2_item_4', 'privacy.s2_item_5'],
  },
  {
    titleKey: 'privacy.s3_title',
    itemKeys: ['privacy.s3_item_1', 'privacy.s3_item_2', 'privacy.s3_item_3', 'privacy.s3_item_4', 'privacy.s3_item_5', 'privacy.s3_item_6'],
  },
  {
    titleKey: 'privacy.s5_title',
    itemKeys: ['privacy.s5_item_1', 'privacy.s5_item_2', 'privacy.s5_item_3', 'privacy.s5_item_4'],
  },
  {
    titleKey: 'privacy.s8_title',
    introKey: 'privacy.s8_intro',
    itemKeys: ['privacy.s8_item_1', 'privacy.s8_item_2', 'privacy.s8_item_3', 'privacy.s8_item_4', 'privacy.s8_item_5', 'privacy.s8_item_6'],
    outroKey: 'privacy.s8_outro',
  },
];

const PROSE_SECTIONS = [
  { titleKey: 'privacy.s1_title', bodyKey: 'privacy.s1_body' },
  { titleKey: 'privacy.s4_title', bodyKey: 'privacy.s4_body' },
  { titleKey: 'privacy.s6_title', bodyKey: 'privacy.s6_body' },
  { titleKey: 'privacy.s7_title', bodyKey: 'privacy.s7_body' },
  { titleKey: 'privacy.s9_title', bodyKey: 'privacy.s9_body' },
  { titleKey: 'privacy.s10_title', bodyKey: 'privacy.s10_body' },
  { titleKey: 'privacy.s11_title', bodyKey: 'privacy.s11_body' },
  { titleKey: 'privacy.s12_title', bodyKey: 'privacy.s12_body' },
];

const SECTION_ORDER = [
  'privacy.s1_title',
  'privacy.s2_title',
  'privacy.s3_title',
  'privacy.s4_title',
  'privacy.s5_title',
  'privacy.s6_title',
  'privacy.s7_title',
  'privacy.s8_title',
  'privacy.s9_title',
  'privacy.s10_title',
  'privacy.s11_title',
  'privacy.s12_title',
];

export default function PrivacyPage() {
  const { t } = useTranslation();
  const proseByTitle = Object.fromEntries(PROSE_SECTIONS.map((s) => [s.titleKey, s]));
  const listByTitle = Object.fromEntries(SECTIONS_WITH_LIST.map((s) => [s.titleKey, s]));

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-8 w-8 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-900">{t('privacy.title')}</h1>
          </div>

          <p className="text-sm text-gray-500 mb-8">{t('privacy.last_updated')}</p>

          <div className="prose prose-sm prose-gray max-w-none space-y-6">
            {SECTION_ORDER.map((titleKey) => {
              const prose = proseByTitle[titleKey];
              const list = listByTitle[titleKey];

              if (prose) {
                return (
                  <section key={titleKey}>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">{t(prose.titleKey)}</h2>
                    <p
                      className="text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: t(prose.bodyKey) }}
                    />
                  </section>
                );
              }

              if (list) {
                return (
                  <section key={titleKey}>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">{t(list.titleKey)}</h2>
                    {list.introKey && (
                      <p className="text-gray-600 leading-relaxed mb-2">{t(list.introKey)}</p>
                    )}
                    <ul className="list-disc pl-5 text-gray-600 space-y-1">
                      {list.itemKeys.map((itemKey) => (
                        <li
                          key={itemKey}
                          dangerouslySetInnerHTML={{ __html: t(itemKey) }}
                        />
                      ))}
                    </ul>
                    {list.outroKey && (
                      <p
                        className="text-gray-600 leading-relaxed mt-2"
                        dangerouslySetInnerHTML={{ __html: t(list.outroKey) }}
                      />
                    )}
                  </section>
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
